"use client";

import type { ButtonProps } from "@rallly/ui/button";
import { Button } from "@rallly/ui/button";
import { useSession } from "next-auth/react";
import React from "react";
import { env } from "@/env";
import { PayWallDialog } from "./components/pay-wall-dialog";

interface BillingContextType {
  showPayWall: () => void;
}

const BillingContext = React.createContext<BillingContextType | null>(null);

interface BillingProviderProps {
  children: React.ReactNode;
}

export function BillingProvider({ children }: BillingProviderProps) {
  const [isPayWallOpen, setIsPayWallOpen] = React.useState(false);

  const showPayWall = React.useCallback(() => {
    setIsPayWallOpen(true);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      showPayWall,
    }),
    [showPayWall],
  );

  return (
    <BillingContext.Provider value={contextValue}>
      {children}
      <PayWallDialog isOpen={isPayWallOpen} onOpenChange={setIsPayWallOpen} />
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = React.useContext(BillingContext);
  if (!context) {
    throw new Error("useBilling must be used within a BillingProvider");
  }
  return context;
}

export const PayWallButton = ({ onClick, ...forwardedProps }: ButtonProps) => {
  const { data: session } = useSession();

  // Generate Wix upgrade URL using user ID as instance ID
  const wixUpgradeUrl = React.useMemo(() => {
    if (!session?.user?.id || !env.NEXT_PUBLIC_WIX_APP_ID) return null;
    
    // Use user ID as instance ID since all users are Wix users
    return `https://www.wix.com/apps/upgrade/${env.NEXT_PUBLIC_WIX_APP_ID}?appInstanceId=${session.user.id}`;
  }, [session?.user?.id]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (wixUpgradeUrl) {
      // Redirect directly to Wix upgrade URL
      window.open(wixUpgradeUrl, "_blank");
    }
    onClick?.(e);
  };

  return (
    <Button
      onClick={handleClick}
      {...forwardedProps}
    />
  );
};
